/**
 * @file 试卷打印块
 * @description 页眉、页脚、模块标题与题目块，样式对齐客户端打印模板。
 *
 * @whenToUse 三套试卷模板的分页结果渲染。
 * @whenNotToUse 后台列表与表单。
 */

import { useState, type CSSProperties } from 'react'
import iconUrl from '@/assets/icon.png'
import genwoxueIcon from '@/assets/paper/genwoxue.svg'
import gongguIcon from '@/assets/paper/gonggu.svg'
import knowledgeIcon from '@/assets/paper/knowledge.svg'
import lianxiIcon from '@/assets/paper/lianxi.svg'
import xiaoshiniudaoIcon from '@/assets/paper/xiaoshiniudao.svg'
import { renderMathHtml } from '@/components/pages/handout-page/print/katex-render.ts'

/** 模块图标 */
export type ModuleIcon = 'knowledge' | 'gonggu' | 'genwoxue' | 'xiaoshiniudao' | 'lianxi'

/** 选择题选项 */
export type ChoiceOption = {
  /** 选项标号 */
  label: string
  /** 选项 HTML */
  valueHtml: string
}

/** 双栏中的一题 */
export type ColumnItem = {
  /** 序号；续页为空 */
  index: number | string
  /** 正文 HTML */
  html: string
  /** 序号前缀 */
  prefix?: string
  /** 序号后缀 */
  suffix?: string
  /** 选择题选项 */
  options?: ChoiceOption[] | null
}

/** 块携带的数据；不同 type 只用其中一部分字段 */
export type BlockData = {
  /** 模块标题 */
  title?: string
  /** 模块说明 */
  description?: string
  /** 模块图标 */
  icon?: ModuleIcon
  /** 答案小标题 */
  subTitle?: string
  /** 题号 */
  index?: number | string
  /** 题干 HTML */
  question?: string
  /** 答案 HTML */
  answerHtml?: string
  /** 讲解 HTML */
  analysisHtml?: string
  /** 讲解续页 */
  analysisContinuation?: boolean
  /** 典例 HTML */
  exampleHtml?: string
  /** 选择题选项 */
  options?: ChoiceOption[] | null
  /** 主观题留白 */
  needPlaceholder?: boolean
  /** 试卷编号 */
  paperNo?: string
  /** 二维码 Data URL */
  qrCodeDataUrl?: string
  /** 页内提示 */
  tipText?: string
}

/** 分页块类型 */
export type BlockTypeName =
  | 'question'
  | 'block_title'
  | 'block_footer'
  | 'finish-note'
  | 'answer-subtitle'
  | 'answer-data'
  | 'answer-prefix'
  | 'paper-tip'
  | 'paper-qr-code'
  | 'coreKnowledgePoints'
  | 'consolidationQuestions'
  | 'learnWithMeList'
  | 'tryPracticeQuestions'
  | 'adaptivePracticeQuestions'
  | 'learn-with-me-vertical'

/** 一页中的一块内容 */
export type PaperBlock = {
  /** 块类型 */
  type: BlockTypeName
  /** 是否模块内首块（样式用 is-last 类名，与客户端一致） */
  isLast?: boolean
  /** 双栏改为整行 */
  fullRow?: boolean
  /** 模块脚注 */
  text?: string
  /** 双栏题目 */
  items?: ColumnItem[]
  /** 块数据 */
  data?: BlockData
}

/** 页眉信息 */
export type PaperHeaderInfo = {
  /** 学案编号 */
  paperNo: string
  /** 学员姓名 */
  studentName: string
  /** 电话 */
  phone: string
  /** 学科 */
  subjectLabel: string
  /** 教育阶段 */
  stageLabel: string
  /** 年级 */
  gradeLabel: string
  /** 学期 */
  semesterLabel: string
  /** 学习力 */
  learningForce: string
  /** 学习力等级 */
  learningType: string
  /** 教材 */
  textbookName: string
}

/** 二维码边长（160px 按 96dpi 换成 mm） */
// const PAPER_QR_SIZE_MM = (160 * 25.4) / 96

const MODULE_ICON: Record<ModuleIcon, string> = {
  knowledge: knowledgeIcon,
  gonggu: gongguIcon,
  genwoxue: genwoxueIcon,
  xiaoshiniudao: xiaoshiniudaoIcon,
  lianxi: lianxiIcon,
}

/**
 * 格式化为页脚时间。
 * @param date 时间
 */
function formatPrintedAt(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

/**
 * 插入已渲染公式的 HTML。
 * @param html 原始 HTML
 * @param className 附加类名
 */
function RichHtml({ html, className }: { html: string; className?: string }) {
  return (
    <div
      className={className ? `rich-html ${className}` : 'rich-html'}
      dangerouslySetInnerHTML={{ __html: renderMathHtml(html) }}
    />
  )
}

/**
 * 选择题选项网格。
 * @param options 选项
 */
function ChoiceGrid({ options }: { options?: ChoiceOption[] | null }) {
  if (!options || options.length === 0) return null
  return (
    <div className="question-choice-grid">
      {options.map((option, index) => (
        <div key={`${option.label}-${index}`} className="question-choice-item">
          <span className="question-choice-label">{option.label}:</span>
          <span
            className="question-choice-value"
            dangerouslySetInnerHTML={{ __html: renderMathHtml(option.valueHtml) }}
          />
        </div>
      ))}
    </div>
  )
}

/**
 * 试卷页眉。
 */
export function PagerHeader({
  info,
  showLearningForce = true,
  style,
}: {
  /** 学员与试卷信息 */
  info: PaperHeaderInfo
  /** 是否展示学习力 */
  showLearningForce?: boolean
  /** 页眉内联样式 */
  style?: CSSProperties
}) {
  const semester = info.semesterLabel ? `（${info.semesterLabel}）` : ''
  return (
    <header className="template-paper-header" style={style}>
      <div className="template-paper-brand">
        <h1 className="template-paper-title">
          <img src={iconUrl} className="template-paper-logo" alt="" />
          <span>思犇数理化</span>
        </h1>
        <div className="template-paper-code">学案编号：{info.paperNo || '--'}</div>
      </div>
      <div className="template-paper-meta">
        <div className="template-paper-meta-card">
          <span className="template-paper-meta-label">姓名：</span>
          <span className="template-paper-meta-value">{info.studentName || '_________'}</span>
        </div>
        <div className="template-paper-meta-card">
          <span className="template-paper-meta-label">电话：</span>
          <span className="template-paper-meta-value">{info.phone || '_________'}</span>
        </div>
        <div className="template-paper-meta-card">
          <span className="template-paper-meta-label">开始/结束时间：</span>
          <span className="meta-underline" />
        </div>
        <div className="template-paper-meta-card">
          <span className="template-paper-meta-value">
            {info.subjectLabel ? `${info.subjectLabel}：` : ''}
            {info.textbookName ? `${info.textbookName} ` : ''}
            {info.stageLabel}
            {info.gradeLabel}
            {semester}
            {info.learningType}
          </span>
        </div>
        {showLearningForce ? (
          <div className="template-paper-meta-card">
            <span className="template-paper-meta-label">学习力：</span>
            <span className="template-paper-meta-value">{info.learningForce || '--'}</span>
          </div>
        ) : null}
      </div>
    </header>
  )
}

/**
 * 试卷页脚。
 */
export function PagerFooter({ page, total }: { page: number; total: number }) {
  const [time] = useState(() => formatPrintedAt(new Date()))
  return (
    <footer className="pager-footer">
      <span>
        第{page}页 / 共{total}页
      </span>
      <span>{time}</span>
    </footer>
  )
}

/**
 * 模块标题。
 */
function ModuleHeader({ data }: { data?: BlockData }) {
  const icon = data?.icon ? MODULE_ICON[data.icon] : ''
  return (
    <div className="template-module-header">
      <div className="template-flex-between">
        <div className="template-module-title">
          {icon ? <img src={icon} className="template-module-icon" alt="" /> : null}
          <span>{data?.title}</span>
        </div>
        {data?.description ? <div className="template-module-desc">{data.description}</div> : null}
      </div>
    </div>
  )
}

/**
 * 普通题目。
 */
function QuestionDetailView({
  data,
  isLast,
  hasBorder,
}: {
  data?: BlockData
  isLast?: boolean
  hasBorder?: boolean
}) {
  const question = data?.question || ''
  const className = [
    'question-detail',
    isLast ? 'is-last' : '',
    data?.needPlaceholder ? 'need-placeholder' : '',
    hasBorder ? 'has-border' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <article className={className}>
      <div className="question-detail-html">
        {question ? (
          <div className="question-column-item_index">{data?.index}.</div>
        ) : null}
        {question ? <RichHtml className="rich-html-detail" html={question} /> : null}
      </div>
      <ChoiceGrid options={data?.options} />
    </article>
  )
}

/**
 * 双栏题目。
 */
function QuestionColumnView({
  block,
  horizontal,
}: {
  block: PaperBlock
  horizontal?: boolean
}) {
  const items = block.fullRow ? (block.items ?? []).filter((item) => item.html) : block.items ?? []
  const className = [
    'question-column',
    block.isLast ? 'is-last' : '',
    block.fullRow ? 'full-row' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <div className={className}>
      {items.map((item, index) => (
        <article
          key={`${item.prefix ?? ''}${item.index}-${index}`}
          className={horizontal ? 'question-column-item horization' : 'question-column-item'}
        >
          {item.html ? (
            <div className="question-column-item_index">
              {item.prefix}
              {item.index}
              {item.suffix ?? '.'}
            </div>
          ) : null}
          {item.html ? <RichHtml html={item.html} /> : null}
          <ChoiceGrid options={item.options} />
        </article>
      ))}
    </div>
  )
}

/**
 * 精简模板的典例 + 讲解竖排。
 */
function LearnVerticalView({ block }: { block: PaperBlock }) {
  const data = block.data
  const continuation = data?.analysisContinuation === true
  const className = [
    'learn-with-me-vertical',
    continuation ? 'is-analysis-continuation' : '',
    block.isLast ? 'is-last' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <div className={className}>
      {continuation ? null : (
        <section className="learn-with-me-example">
          <div className="learn-with-me-example-header">典例{data?.index}：</div>
          <RichHtml html={data?.exampleHtml || ''} />
          <ChoiceGrid options={data?.options} />
        </section>
      )}
      <section className="learn-with-me-analysis">
        <div className="learn-with-me-analysis-head">
          {continuation ? '讲解（续）：' : '讲解：'}
        </div>
        <div className="learn-with-me-analysis-body">
          <RichHtml html={data?.analysisHtml || '<p>暂无讲解</p>'} />
        </div>
      </section>
    </div>
  )
}

/**
 * 答案行。
 */
function AnswerView({ data }: { data?: BlockData }) {
  const continuation = data?.analysisContinuation === true
  return (
    <div className={continuation ? 'answer-row-wrap answer-analysis-continuation' : 'answer-row-wrap'}>
      <div className="answer-item">
        {continuation ? null : <span className="answer-index">{data?.index}.</span>}
        <div className="answer-body">
          {continuation ? null : (
            <div className="answer-block">
              <span className="answer-label">答案：</span>
              <RichHtml html={data?.answerHtml || '<p>暂无答案</p>'} />
            </div>
          )}
          <div className="answer-block">
            <span className="answer-label">{continuation ? '讲解（续）：' : '讲解：'}</span>
            <RichHtml html={data?.analysisHtml || '<p>暂无讲解</p>'} />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * 渲染单个分页块。
 * @param block 分页块
 * @param hasBorder 阶段卷题目带边框
 */
export function BlockView({ block, hasBorder }: { block: PaperBlock; hasBorder?: boolean }) {
  switch (block.type) {
    case 'block_title':
      return <ModuleHeader data={block.data} />
    case 'block_footer':
      return <div className="module-footer-text">{block.text}</div>
    case 'finish-note':
      return (
        <div className="finish-note">
          <div className="finish-note-title">我还是可以的嘛完成作答了!!!</div>
          <div className="finish-note-line">
            <span className="finish-note-label">收获了什么:</span>
            <span className="finish-note-underline" />
          </div>
        </div>
      )
    case 'answer-subtitle':
      return <div className="answer-subtitle">{block.data?.subTitle}</div>
    case 'answer-prefix':
      return (
        <div className="module-footer-text left">
          <p>正确的看答案流程：</p>
          <p>第一、看明白怎么做。</p>
          <p>第二、想明白为什么要这么做。</p>
          <p>第三、捋清楚我怎么才能想到要这样做。</p>
        </div>
      )
    case 'answer-data':
      return <AnswerView data={block.data} />
    case 'paper-tip':
      return block.data?.tipText ? <p className="template-paper-tip">{block.data.tipText}</p> : null
    case 'paper-qr-code':
      return null;
      // return (
      //   <div className="paper-qr-code" style={{ minHeight: `${PAPER_QR_SIZE_MM}mm` }}>
      //     <img
      //       src={block.data?.qrCodeDataUrl}
      //       alt={`试卷 ${block.data?.paperNo ?? ''} 二维码`}
      //       width={200}
      //       height={200}
      //       style={{ width: `${PAPER_QR_SIZE_MM}mm`, height: `${PAPER_QR_SIZE_MM}mm` }}
      //     />
      //     <p className="paper-qr-code-tip">扫描二维码查看答案解析</p>
      //   </div>
      // )
    case 'question':
      return <QuestionDetailView data={block.data} isLast={block.isLast} hasBorder />
    case 'tryPracticeQuestions':
    case 'adaptivePracticeQuestions':
      return <QuestionDetailView data={block.data} isLast={block.isLast} hasBorder={hasBorder} />
    case 'learnWithMeList':
      return <QuestionColumnView block={block} horizontal />
    case 'coreKnowledgePoints':
    case 'consolidationQuestions':
      return <QuestionColumnView block={block} />
    case 'learn-with-me-vertical':
      return <LearnVerticalView block={block} />
    default:
      return null
  }
}
